/**
 * Per-build generation budget (MANDATE 34, P0 cost governance) - PURE, browser-safe core.
 *
 * The audit proved a single build can fan out to many real external provider calls across TWO
 * provider abstractions (aiRouter for schema + code gen; aiCompletionGateway for synthesis), with
 * NO shared ceiling, and that aiRouter races two providers in parallel (double spend). Because the
 * ACU refund is only an internal ledger entry, every one of those calls is billed by the provider
 * regardless of whether the build ultimately fails. That is unbounded credit burn.
 *
 * This module holds the REAL, shared, per-build ceiling as PURE logic with NO Node-only imports, so
 * it can be bundled into the browser graph (aiRouter is reachable from the frontend) without pulling
 * `async_hooks` into the Vite build. The AsyncLocalStorage wiring that makes ONE budget ambient
 * across every provider call in a build lives in the server-only companion `generationBudgetContext.ts`,
 * which registers itself here via `registerAmbientBudget`. Outside a build (browser, or a server
 * process where no build is in flight) the ambient lookup returns undefined, so `reserveProviderCall`
 * and `settleProviderCall` are no-ops and non-build callers are unaffected.
 */

export interface GenerationBudgetLimits {
  /** Hard cap on total external provider CALLS in one build (racing counts each racer). */
  maxProviderCalls: number;
  /** Hard cap on estimated provider spend (USD) in one build. */
  maxCostUsd: number;
  /** Wall-clock cap for the whole generation (ms). */
  maxDurationMs: number;
}

/** Capacity planning inputs (all env-overridable at the call site; exposed for tests). */
export interface CapacityPlanOptions {
  callsPerUnit?: number;
  msPerUnit?: number;
  overheadCalls?: number;
  maxCalls?: number;
  maxDurationMs?: number;
  /** Estimated provider spend per unit, used to size capacity against the MONEY ceiling too. */
  costPerUnitUsd?: number;
}

/** What one build can actually take on, given the real decomposition. */
export interface CapacityPlan {
  /** True when the WHOLE plan fits in this one build. */
  fits: boolean;
  /** Units in the real decomposition. */
  units: number;
  /** Units this build can realistically finish, from whichever ceiling binds first. */
  unitsThisBuild: number;
}

export type BudgetExceededReason = "calls" | "cost" | "time";

export class GenerationBudgetExceeded extends Error {
  constructor(public readonly reason: BudgetExceededReason, message: string) {
    super(message);
    this.name = "GenerationBudgetExceeded";
  }
}

/**
 * Coerce budget limits into real bounds. Non-finite (NaN/Infinity) or non-positive values collapse to
 * the conservative fallbacks below, never to "unbounded". Pure and total; exported for direct testing.
 *
 * The fallbacks are deliberately the tightest sensible ones rather than the generous production
 * ceilings: a build running under a limit nobody configured should be bounded hard, and the operator
 * should notice, not silently get the most permissive setting Nuvra has.
 */
/**
 * A finite USD amount from an environment variable, for the pure/browser-safe callers that read their
 * own per-call cost estimates. `Number("$0.05")` is NaN, and `Math.max(0, NaN)` is ALSO NaN, so the
 * usual clamp does not save it: a NaN estimate reaches reserveCall, poisons the running cost total to
 * NaN, and from that moment every `spent + est > cap` comparison is false. One malformed variable
 * therefore disables the per-build money ceiling for the whole build. Never returns NaN.
 */
export function envCostUsd(raw: string | undefined, fallback: number): number {
  // An UNSET or blank variable is the normal case and must yield the fallback. Coercing it through
  // Number("") would return 0, which is not "no override", it is "this call is free": a zero estimate
  // makes the cost ceiling unreachable just as surely as a NaN one does.
  const text = String(raw ?? "").trim();
  if (text === "") return fallback;
  const n = Number(text.replace(/^\$/, "").replace(/,/g, ""));
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export const SAFE_BUDGET_FALLBACKS: GenerationBudgetLimits = {
  maxProviderCalls: 10,
  maxCostUsd: 2.0,
  maxDurationMs: 300_000,
};

export function sanitizeBudgetLimits(limits: GenerationBudgetLimits): GenerationBudgetLimits {
  const positive = (v: unknown, fallback: number): number =>
    typeof v === "number" && Number.isFinite(v) && v > 0 ? v : fallback;
  return {
    maxProviderCalls: Math.max(1, Math.floor(positive(limits?.maxProviderCalls, SAFE_BUDGET_FALLBACKS.maxProviderCalls))),
    maxCostUsd: positive(limits?.maxCostUsd, SAFE_BUDGET_FALLBACKS.maxCostUsd),
    maxDurationMs: Math.max(1_000, Math.floor(positive(limits?.maxDurationMs, SAFE_BUDGET_FALLBACKS.maxDurationMs))),
  };
}

export class GenerationBudget {
  private calls = 0;
  private costUsd = 0;
  /** Outstanding pre-reservations: a caller (e.g. the implementation loop) that reserves a call slot
   *  BEFORE the provider gateway does, so the gateway consumes the pre-reservation instead of counting
   *  the same physical call twice. Guarantees exactly-once accounting per provider call. */
  private preReserved = 0;
  /** Provider calls that were reserved but then FAILED at the provider (the reservation stands, so the
   *  budget still counts the attempt; this is the count for the honest cost/failure report). */
  private failures = 0;
  /** An economic-authorization tightening of the USD cap (Control C-2): once the real build is priced, the
   *  per-build provider ceiling is lowered to the customer-authorized economic value plus a bounded
   *  platform risk allowance, so provider spend can never exceed authorization up to the global cap. Only
   *  ever LOWERS the effective cap (never raises it above limits.maxCostUsd). undefined => no tightening. */
  private costCapOverrideUsd: number | undefined;
  /**
   * Capacity raised to fit the REAL decomposition (see planCapacityForUnits).
   *
   * The default ceilings are sized for an ordinary build. A large product legitimately decomposes into
   * dozens of units, and each unit is at least one provider call, so a fixed 60-call / 30-minute ceiling
   * guarantees that a big build is authorized, charged, and THEN killed by a limit it could never have
   * cleared. Sizing the ceiling to the plan is what makes an authorized build physically completable.
   * Only these two scale; the USD cap never rises, because money is the customer's authorization.
   */
  private callCapOverride: number | undefined;
  private durationCapOverrideMs: number | undefined;
  private readonly startedAt: number;

  public readonly limits: GenerationBudgetLimits;

  constructor(
    limits: GenerationBudgetLimits,
    public readonly buildId?: string,
    now: number = Date.now(),
  ) {
    // A MALFORMED LIMIT MUST NEVER MEAN "NO LIMIT".
    //
    // checkLimits asks `spent + est > cap`. Every comparison against NaN is false, so a cap that
    // arrived as NaN does not clamp this build low, it removes the ceiling and authorizes unbounded
    // provider spend. That is exactly what a deployment environment holding NUVRA_FACTORY_MAX_COST_USD
    // as "$3" instead of "3" produced: `Number("$3")` is NaN, and the per-build hard money ceiling
    // silently stopped existing.
    //
    // The environment is parsed safely at the call sites (envUsdCeiling), and this is the second line:
    // whatever a caller passes, a non-finite or negative bound collapses to the conservative default
    // rather than to infinity, so no configuration path anywhere can disable a ceiling by accident.
    this.limits = sanitizeBudgetLimits(limits);
    this.startedAt = now;
  }

  get callsMade(): number { return this.calls; }
  get costSpentUsd(): number { return this.costUsd; }
  get providerFailures(): number { return this.failures; }
  remainingCalls(): number { return Math.max(0, this.effectiveMaxProviderCalls() - this.calls); }
  remainingCostUsd(): number { return Math.max(0, this.limits.maxCostUsd - this.costUsd); }

  /** Record a provider call that was reserved but failed at the provider (for the honest cost report). */
  noteProviderFailure(): void { this.failures++; }

  /** The effective USD ceiling: the lower of the configured global cap and any economic-authorization
   *  tightening. This is what checkLimits enforces before every provider call. */
  effectiveMaxCostUsd(): number {
    return this.costCapOverrideUsd === undefined
      ? this.limits.maxCostUsd
      : Math.min(this.limits.maxCostUsd, this.costCapOverrideUsd);
  }

  /**
   * Tighten the per-build provider USD ceiling to a specific economic-authorization bound (Control C-2).
   * Monotonic: it only ever LOWERS the effective cap (a higher value than the current effective cap is
   * ignored), so a build's provider exposure is bounded by the customer's authorization, never expanded.
   * Called once the real decomposition is priced (estProviderCost + a bounded platform risk allowance).
   */
  tightenCostCap(usd: number): void {
    if (!(Number.isFinite(usd) && usd >= 0)) return;
    const current = this.effectiveMaxCostUsd();
    if (usd < current) this.costCapOverrideUsd = usd;
  }

  /** The effective provider-call ceiling: the configured cap, or the capacity planned for the real
   *  decomposition when that is larger. */
  effectiveMaxProviderCalls(): number {
    return Math.max(this.limits.maxProviderCalls, this.callCapOverride ?? 0);
  }

  /** The effective wall-clock ceiling, same rule as the call ceiling. */
  effectiveMaxDurationMs(): number {
    return Math.max(this.limits.maxDurationMs, this.durationCapOverrideMs ?? 0);
  }

  /**
   * Size this budget's CALL and TIME ceilings to a real decomposition, and report honestly whether the
   * whole plan fits in one build.
   *
   * Why this exists. A production build decomposed into 34 units against a fixed 60-call / 30-minute
   * ceiling: every unit is at least one provider call, so the plan needed roughly 40 to 70 calls and,
   * at real generation speed, far more than 30 minutes. The build was priced, authorized, charged, and
   * then run into a wall it could never have cleared, which the customer experiences as a long silence
   * followed by "the build ran longer than the time allowed". Authorizing work that cannot physically
   * complete is the defect; this makes the ceiling a function of the plan.
   *
   * It only ever RAISES the two ceilings, and only up to hard maxima, so this is capacity planning and
   * never an escape hatch. The USD cap is deliberately untouched: money stays bounded by what the
   * customer authorized, so a bigger plan buys more time and more calls, never more spend.
   *
   * `fits: false` is not a failure. Work is preserved at the boundary and the customer can continue it
   * (see the resume path), so the honest response is to TELL them up front that it will take more than
   * one build, not to refuse it and not to let them discover it 30 minutes in.
   */
  planCapacityForUnits(unitCount: number, opts: CapacityPlanOptions = {}): CapacityPlan {
    const units = Math.max(0, Math.floor(unitCount));
    const callsPerUnit = opts.callsPerUnit ?? Number(process.env.NUVRA_GEN_CALLS_PER_UNIT ?? 2);
    const msPerUnit = opts.msPerUnit ?? Number(process.env.NUVRA_GEN_MS_PER_UNIT ?? 150_000);
    // Contract extraction, manifest work, and the bounded repair attempts all draw on the same budget.
    const overheadCalls = opts.overheadCalls ?? Number(process.env.NUVRA_GEN_OVERHEAD_CALLS ?? 10);
    const maxCalls = opts.maxCalls ?? Number(process.env.NUVRA_GEN_HARD_MAX_CALLS ?? 240);
    const maxDurationMs = opts.maxDurationMs ?? Number(process.env.NUVRA_GEN_HARD_MAX_MS ?? 2_700_000); // 45 min
    const costPerUnitUsd = Math.max(0, opts.costPerUnitUsd ?? Number(process.env.NUVRA_GEN_COST_PER_UNIT_USD ?? 0.12));

    const wantCalls = units * callsPerUnit + overheadCalls;
    const wantMs = units * msPerUnit;

    this.callCapOverride = Math.min(maxCalls, Math.max(this.limits.maxProviderCalls, wantCalls));
    this.durationCapOverrideMs = Math.min(maxDurationMs, Math.max(this.limits.maxDurationMs, wantMs));

    // MONEY IS A CEILING TOO, AND IT USUALLY BINDS FIRST.
    //
    // Capacity was sized from calls and time only. A founder was told "about 18 of 38 components this
    // time" and got 3, because the provider USD ceiling ran out long before either of those. Promising
    // 18 and delivering 3 is worse than promising 3: the customer plans around the number we gave them.
    // The money ceiling is now part of the same estimate, so the promise matches what the build can
    // actually pay for.
    const budgetUsd = this.effectiveMaxCostUsd() - this.costUsd;
    const unitsByMoney = costPerUnitUsd > 0 ? Math.floor(Math.max(0, budgetUsd) / costPerUnitUsd) : Number.POSITIVE_INFINITY;

    // TIME HEADROOM: units may not spend the WHOLE clock.
    //
    // Capacity planned units against 100% of the time ceiling (45 min / 150s per unit = exactly 18
    // units = exactly 45 minutes), leaving nothing for the work that is not a unit: contract analysis,
    // architecture planning, repository integrity, starting the app, behavioral verification, the repair
    // loop and publishing. So a large build was MATHEMATICALLY CERTAIN to be killed by the clock: it
    // planned to use every second it had before the real remaining stages had even begun. A founder
    // watched exactly that ("the build ran longer than the time allowed"), repeatedly.
    //
    // Reserving the tail turns a guaranteed timeout into a clean stop: fewer units are attempted, they
    // finish, and everything after them still has room to run. Work stops on a unit boundary with the
    // repository preserved, which is a build the customer can continue rather than one that died.
    const unitTimeFraction = Math.min(1, Math.max(0.2, Number(process.env.NUVRA_GEN_UNIT_TIME_FRACTION ?? 0.6)));
    const unitsByCalls = Math.max(0, Math.floor((this.effectiveMaxProviderCalls() - overheadCalls) / Math.max(1, callsPerUnit)));
    const unitsByTime = Math.floor((this.effectiveMaxDurationMs() * unitTimeFraction) / Math.max(1, msPerUnit));
    const affordable = Math.min(units, unitsByCalls, unitsByTime, unitsByMoney);
    const fits = wantCalls <= this.effectiveMaxProviderCalls() && wantMs <= this.effectiveMaxDurationMs() * unitTimeFraction && affordable >= units;
    return { fits, units, unitsThisBuild: Math.max(1, affordable) };
  }

  /**
   * The honest cost/accounting report for a build. Every field is real budget state (never fabricated):
   * how many provider calls were made against the cap, the settled cost against the cap, provider
   * failures, and the elapsed wall-clock against the time cap.
   */
  report(now: number = Date.now()): {
    callsMade: number; maxCalls: number; remainingCalls: number;
    costSettledUsd: number; maxCostUsd: number; remainingCostUsd: number;
    providerFailures: number; elapsedMs: number; maxDurationMs: number;
  } {
    return {
      callsMade: this.calls,
      maxCalls: this.effectiveMaxProviderCalls(),
      remainingCalls: this.remainingCalls(),
      costSettledUsd: this.costUsd,
      maxCostUsd: this.limits.maxCostUsd,
      remainingCostUsd: this.remainingCostUsd(),
      providerFailures: this.failures,
      elapsedMs: now - this.startedAt,
      maxDurationMs: this.effectiveMaxDurationMs(),
    };
  }

  /** The shared limit checks (time / call-cap / cost-cap). Throws before counting anything. */
  /** A cost that is not a finite non-negative number contributes 0, never NaN. A NaN in `costUsd`
   *  makes every subsequent ceiling comparison false, which silently removes the ceiling. */
  private static safeCost(usd: number): number {
    return typeof usd === "number" && Number.isFinite(usd) && usd > 0 ? usd : 0;
  }

  private checkLimits(estCostUsd: number, now: number): void {
    // Enforce the EFFECTIVE ceilings, which are the configured ones until a real decomposition raises
    // them (planCapacityForUnits). Enforcing the raw configured value here would re-impose the fixed
    // limit that a large authorized build cannot clear.
    const maxMs = this.effectiveMaxDurationMs();
    if (now - this.startedAt > maxMs) {
      throw new GenerationBudgetExceeded("time", `generation exceeded the ${maxMs}ms time budget`);
    }
    const maxCalls = this.effectiveMaxProviderCalls();
    if (this.calls >= maxCalls) {
      throw new GenerationBudgetExceeded("calls", `generation reached the limit of ${maxCalls} provider calls`);
    }
    const capUsd = this.effectiveMaxCostUsd();
    if (this.costUsd + GenerationBudget.safeCost(estCostUsd) > capUsd) {
      throw new GenerationBudgetExceeded("cost", `generation would exceed the $${capUsd} provider budget`);
    }
  }

  /**
   * Reserve ONE provider call BEFORE making it. Throws GenerationBudgetExceeded if a hard limit is
   * already reached or the estimated cost would breach the budget, so the call is never made. On
   * success it provisionally counts the call + estimated cost (settled to the real cost afterward).
   *
   * Exactly-once: if a pre-reservation is outstanding (an upstream loop already reserved the slot for
   * this same physical call), this CONSUMES it and does NOT count the call a second time.
   */
  reserveCall(estCostUsd = 0, now: number = Date.now()): void {
    if (this.preReserved > 0) { this.preReserved--; return; } // the pre-reservation already counted this call
    this.checkLimits(estCostUsd, now);
    this.calls++;
    this.costUsd += GenerationBudget.safeCost(estCostUsd);
  }

  /**
   * PRE-reserve a provider call slot at a boundary that runs BEFORE the provider gateway (e.g. the
   * implementation/repair loop, which reserves one slot per unit/attempt). It counts the call once here;
   * the subsequent gateway reserveCall() for the same physical call consumes this pre-reservation
   * instead of double-counting. If the physical call is never made (e.g. a mock that skips the gateway),
   * the slot stays counted, which is correct: the loop intended to make one call.
   */
  preReserveCall(estCostUsd = 0, now: number = Date.now()): void {
    this.checkLimits(estCostUsd, now);
    this.calls++;
    this.costUsd += GenerationBudget.safeCost(estCostUsd);
    this.preReserved++;
  }

  /** Replace the provisional estimate with the real provider cost after the call returns. */
  settle(estCostUsd: number, actualCostUsd: number): void {
    this.costUsd += (GenerationBudget.safeCost(actualCostUsd) - GenerationBudget.safeCost(estCostUsd));
    if (!Number.isFinite(this.costUsd) || this.costUsd < 0) this.costUsd = 0;
  }

  snapshot() {
    return { calls: this.calls, costUsd: this.costUsd, limits: this.limits, buildId: this.buildId };
  }
}

/**
 * Ambient-budget seam. The server-only context module registers a resolver that reads the current
 * build's budget from AsyncLocalStorage. When nothing is registered (browser bundle, or a server
 * process with no build in flight), the resolver stays null and every helper below is a no-op.
 */
export interface AmbientBudgetResolver {
  current(): GenerationBudget | undefined;
}

let ambient: AmbientBudgetResolver | null = null;

/** Called once by the server-only context module to wire the AsyncLocalStorage-backed resolver. */
export function registerAmbientBudget(resolver: AmbientBudgetResolver): void {
  ambient = resolver;
}

export function currentGenerationBudget(): GenerationBudget | undefined {
  return ambient?.current();
}

/** Reserve a provider call against the ambient build budget, if one is set. No-op outside a build.
 *  Consumes an outstanding pre-reservation (exactly-once) when one exists. */
export function reserveProviderCall(estCostUsd = 0): void {
  ambient?.current()?.reserveCall(estCostUsd);
}

/** PRE-reserve a provider call slot at a pre-gateway boundary (implementation/repair loop). The later
 *  gateway reserveProviderCall for the same physical call consumes it, so a call counts exactly once.
 *  No-op outside a build. */
export function preReserveProviderCall(estCostUsd = 0): void {
  ambient?.current()?.preReserveCall(estCostUsd);
}

/** Settle the real provider cost against the ambient build budget, if one is set. No-op otherwise. */
export function settleProviderCall(estCostUsd: number, actualCostUsd: number): void {
  ambient?.current()?.settle(estCostUsd, actualCostUsd);
}

/** Note a provider-call FAILURE against the ambient build budget, if one is set. No-op otherwise. */
export function noteProviderFailure(): void {
  ambient?.current()?.noteProviderFailure();
}

/** Env-configurable default limits. Deliberately conservative so a build can never loop and burn
 *  unbounded credits; the owner can raise them per environment. */
export function defaultGenerationBudgetLimits(): GenerationBudgetLimits {
  return sanitizeBudgetLimits({
    maxProviderCalls: Math.max(1, envCostUsd(process.env.NUVRA_GEN_MAX_PROVIDER_CALLS, 10)),
    maxCostUsd:       Math.max(0.01, envCostUsd(process.env.NUVRA_GEN_MAX_COST_USD, 2.0)),
    maxDurationMs:    Math.max(10_000, envCostUsd(process.env.NUVRA_GEN_MAX_DURATION_MS, 300_000)),
  });
}
